import type {
  CNSWorkflowStage,
} from "@/models/cns";

export const WORKFLOW_STAGE_ORDER: readonly CNSWorkflowStage[] = [
  "DISCOVERY",
  "DRAFT",
  "TRACKER",
  "RUNNING",
  "COMPLETED",
  "INSIGHTS",
  "LEARNING",
] as const;

export type StageStepTodoStatus =
  | "pending"
  | "in_progress"
  | "completed"
  | "cancelled";

export interface StageStepTodo {
  id: string;
  label: string;
  status: StageStepTodoStatus;
}

export const STAGE_LABEL: Record<CNSWorkflowStage, string> = {
  DISCOVERY: "Discovery",
  DRAFT: "Experiment design",
  TRACKER: "Data tracker",
  RUNNING: "Running",
  COMPLETED: "Wrap-up",
  INSIGHTS: "Insights",
  LEARNING: "Learning",
};

export const ACTION_TYPE_LABEL: Readonly<Record<string, string>> = {
  sources: "Attach sources",
  question_answer: "Answer clarifying questions",
  assumption_selection: "Select assumptions",
  hypothesis_selection: "Select hypothesis",
  experiment_card_selection: "Select experiment",
  assign_experiment: "Assign experiment",
  approve_experiment: "Approve experiment",
  setup_tracker: "Set up tracker",
  lock_tracker: "Lock tracker",
  mark_complete: "Mark complete",
  insights_draft: "Review insights",
  learning_gate: "Review evidence",
  learning_qa: "Complete Learning draft",
  learning_card: "Confirm Learning",
  connect_source: "Connect source",
};

function stageStatus(
  index: number,
  currentIndex: number,
  hasCurrentWork: boolean,
): StageStepTodoStatus {
  if (currentIndex < 0 || index > currentIndex) return "pending";
  if (index < currentIndex) return "completed";
  // Current stage. A resting stage without an active workflow request is done,
  // while a stage with current work remains in progress.
  return hasCurrentWork ? "in_progress" : "completed";
}

/**
 * Maps workflow stage to stepper todos for the artifact panel.
 * Advances on stage transitions only (7 stages), not per pending-human-action card resolve.
 * The current stage renders completed (not in_progress) when no active workflow request remains,
 * so the in-product terminal Learning phase shows all checkmarks instead of a spinner.
 */
export function stageTodosFromSnapshot(
  currentStage: CNSWorkflowStage | undefined,
  hasCurrentWork: boolean,
): StageStepTodo[] {
  const currentIndex = currentStage
    ? WORKFLOW_STAGE_ORDER.indexOf(currentStage)
    : -1;

  return WORKFLOW_STAGE_ORDER.map((stage, index) => ({
    id: stage,
    label: STAGE_LABEL[stage],
    status: stageStatus(index, currentIndex, hasCurrentWork),
  }));
}
